import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Truck, Info } from "@phosphor-icons/react";
import api, { money } from "../lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const PERIODS = [["fy", "This financial year"], ["12m", "Last 12 months"], ["all", "All time"]];

export const compact = (n) => {
  const a = Math.abs(n);
  const fmt = (v, s) => `${parseFloat(v.toFixed(1))}${s}`;
  if (a >= 1e9) return fmt(n / 1e9, "B");
  if (a >= 1e6) return fmt(n / 1e6, "M");
  if (a >= 1e3) return fmt(n / 1e3, "K");
  return `${Math.round(n)}`;
};

export const SuppliersBySpend = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("fy");
  const { data, isLoading } = useQuery({ queryKey: ["vendor-spend", period], queryFn: async () => (await api.get("/vendor-spend", { params: { period } })).data });
  const items = data?.items || [];
  const max = Math.max(1, ...items.map((i) => i.spend));
  return (
    <div className="border border-border bg-card rounded-xl shadow-sm px-6 py-5 mb-6 max-w-3xl" data-testid="suppliers-by-spend">
      <div className="flex flex-col items-center gap-1 mb-4">
        <Truck size={24} />
        <div className="flex items-center gap-1.5">
          <h3 className="font-bold text-lg">Suppliers by Spend</h3>
          <TooltipProvider delayDuration={150}><Tooltip>
            <TooltipTrigger asChild><button aria-label="About this chart" data-testid="spend-info"><Info size={16} /></button></TooltipTrigger>
            <TooltipContent className="max-w-xs">Posted purchase invoices minus purchase credit memos, in AUD.</TooltipContent>
          </Tooltip></TooltipProvider>
        </div>
        <div className="flex gap-1 mt-1" role="tablist">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`spend-period-${k}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${period === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      </div>
      {isLoading ? <p className="text-sm text-muted-foreground text-center">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4" data-testid="spend-empty">No supplier spend in this period yet.</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((v) => (
            <button key={v.vendor_id} onClick={() => navigate(`/purchasing/vendor/${v.vendor_id}`)} data-testid={`spend-row-${v.vendor_id}`}
              className="w-full grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-4 text-left group" title={`${v.name}: ${money(v.spend)}`}>
              <span className="truncate text-base group-hover:text-primary transition-colors" data-no-translate>{v.name}</span>
              <span className="flex items-center gap-2 min-w-0">
                <span className="h-3.5 rounded-full bg-sky-300 dark:bg-sky-500 transition-[width] duration-500" style={{ width: `${Math.max(4, (v.spend / max) * 80)}%` }} />
                <span className="text-sm font-semibold tabular shrink-0" data-testid={`spend-amount-${v.vendor_id}`}>{compact(v.spend)}</span>
              </span>
            </button>
          ))}
          <p className="text-xs text-muted-foreground text-right pt-1" data-testid="spend-total">{data.vendors} suppliers · {money(data.total)} total</p>
        </div>
      )}
    </div>
  );
};
